import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {makeStyles} from "@material-ui/core/styles";
import UserAvatar from "../../../../user-avatar/UserAvatar";
import {addGroup, addGroupGuest, addGroupProvider} from "../../../../../api/pro-guest-functions";
import {useApiNotification} from "../../../../ui/ApiNotificationProvider";
import * as _ from "lodash";
import {FormStatus, UserItem, UserRowData, WithErrorAndProcessingStatus} from "../../../../../models/shared";
import {NavigationProps, WizardNavigation, WizardNavigationButton} from "../../../../_shared/wizard/WizardNavigation";
import {ValidationRow} from "../../../../_shared/wizard/ValidationRow";
import {Provider} from "../../../../../models/pro-guest.models";

const useStyles = makeStyles(theme => ({
    actionContainer: {
        width: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
    },
    sectionTitle: {
        fontWeight: 'bold'
    },
    groupDataContainer: {
        padding: theme.spacing(2),
    }
}));

interface Props extends NavigationProps {
    form: { name: string, guests: UserItem[], providers: Provider[] },
    organizationId: number,
    folderId: number
}

interface GuestWithErrorAndProcessingStatus extends WithErrorAndProcessingStatus {
    guestInfo: UserRowData & { id: number }
}

interface ProviderWithErrorAndProcessingStatus extends WithErrorAndProcessingStatus {
    providerInfo: Provider
}


export const Step4Validation: React.FC<Props> = ({form, organizationId, folderId, ...props}) => {
    const {t, i18n} = useTranslation();
    const classes = useStyles();
    const {showSuccess, handleError} = useApiNotification();
    const [displayedForm, setDisplayedForm] = useState({
        groupName: {
            name: '',
            processingStatus: FormStatus.none
        },
        guests: [] as GuestWithErrorAndProcessingStatus[],
        providers: [] as ProviderWithErrorAndProcessingStatus[]
    });
    useEffect(() => {
        setDisplayedForm({
            groupName: {name: form.name, processingStatus: FormStatus.none},
            guests: form.guests.map((g: UserItem) => ({
                guestInfo: {...g.data, id: g.id},
                processingStatus: FormStatus.none,
                errorMessage: ''
            })),
            providers: form.providers.map((p: any) => ({
                providerInfo: {...p},
                processingStatus: FormStatus.none,
                errorMessage: ''
            })),
        });
    }, [form]);

    const isCompleted = (status: any) => status === FormStatus.successful || status === FormStatus.failed;

    const allCompleted = isCompleted((displayedForm.groupName as any).processingStatus)
        && displayedForm.providers.every(p => isCompleted((p as any).processingStatus))
        && displayedForm.guests.every(g => isCompleted((g as any).processingStatus));

    const providerRow = (provider: any) =>
        <ValidationRow key={`validation-pro-${provider.providerInfo.userId}`}
                       processingStatus={provider.processingStatus}>
            <Grid container item xs={11} spacing={4}>
                <Grid item className={(classes as any).avatarColumn}>
                    <UserAvatar userId={provider.providerInfo.userId}/>
                </Grid>
                <Grid item>
                    <Typography
                        variant="body1">{provider.providerInfo.firstName} {provider.providerInfo.lastName}</Typography>
                    <Typography variant="body2" color={'textPrimary'}>{provider.providerInfo.email}</Typography>
                </Grid>
            </Grid>
        </ValidationRow>;

    const guestRow = (guest: any) =>
        <ValidationRow key={`validation-guest-${guest.guestInfo.id}`}
                       processingStatus={guest.processingStatus}
                       errorMessage={guest.errorMessage}>
            <Grid container item xs={11} spacing={4}>
                <Grid item>
                    <Typography variant="body1">{guest.guestInfo.email}</Typography>
                </Grid>
                <Grid item>
                    <Typography variant="body1">{guest.guestInfo.firstName}</Typography>
                </Grid>
                <Grid item>
                    <Typography variant="body1">{guest.guestInfo.lastName}</Typography>
                </Grid>
            </Grid>
        </ValidationRow>;


    const onSave = async () => {
        let groupId;
        setDisplayedForm(state => ({
            ...state,
            groupName: {...state.groupName, processingStatus: FormStatus.processing}
        }));
        await addGroup({organizationId, folderId, group: {name: (displayedForm.groupName as any).name}})
            .then((group) => {
                setDisplayedForm(state => ({
                    ...state,
                    groupName: {...state.groupName, processingStatus: FormStatus.successful}
                }));
                groupId = group.id;
            })
            .catch((e) => {
                setDisplayedForm(state => ({
                    ...state,
                    groupName: {...state.groupName, processingStatus: FormStatus.failed}
                }));
                handleError(e);
            });
        if (!groupId) {
            return;
        }
        for (let index in displayedForm.providers) {
            const provider = displayedForm.providers[index];
            setDisplayedForm(state => {
                (state.providers[index] as any).processingStatus = FormStatus.processing;
                return {...state, providers: state.providers};
            });
            await addGroupProvider({organizationId, folderId, groupId, providerId: (provider as any).providerInfo.id})
                .then(() => {
                    setDisplayedForm(state => {
                        (state.providers[index] as any).processingStatus = FormStatus.successful;
                        return {...state, providers: state.providers};
                    });
                })
                .catch(err => {
                    setDisplayedForm(state => {
                        (state.providers[index] as any).processingStatus = FormStatus.failed;
                        return {...state, providers: state.providers};
                    });
                });
        }
        for (let index in displayedForm.guests) {
            const guest = displayedForm.guests[index];
            setDisplayedForm(state => {
                (state.guests[index] as any).processingStatus = FormStatus.processing;
                return {...state, guests: state.guests};
            });
            const guestToAdd = {
                email: (guest as any).guestInfo.email,
                firstName: (guest as any).guestInfo.firstName,
                lastName: (guest as any).guestInfo.lastName,
                language: i18n.language
            };
            await addGroupGuest({organizationId, folderId, groupId, guest: guestToAdd})
                .then(() => {
                    setDisplayedForm(state => {
                        (state.guests[index] as any).processingStatus = FormStatus.successful;
                        return {...state, guests: state.guests};
                    });
                })
                .catch(err => {
                    const errorType = _.get(err, 'response.data.error.type') || 'UnknownError';
                    const msg = `_Common.Api.Messages.${errorType}`;
                    setDisplayedForm(state => {
                        (state.guests[index] as any).processingStatus = FormStatus.failed;
                        (state.guests[index] as any).errorMessage = msg;
                        return {...state, guests: state.guests};
                    });
                });
        }
        showSuccess();
    };

    const getDisabledButtons = (): Set<WizardNavigationButton> => {
        let disabledButtons = new Set<WizardNavigationButton>();
        if (displayedForm.groupName.processingStatus !== FormStatus.none) {
            disabledButtons = new Set([WizardNavigationButton.Previous, WizardNavigationButton.Next, WizardNavigationButton.Save]);
            if (!allCompleted) {
                disabledButtons.add(WizardNavigationButton.Close);
            }
        }
        return disabledButtons;
    }

    return <Grid container spacing={3}>
        <Grid item xs={12}>
            <Typography variant="body1">{t('ProGuest.Groups.Tabs.Validation.Description')}</Typography>
        </Grid>
        <Grid container item xs={12}>
            <Typography variant="body1" className={classes.sectionTitle}>
                {t('ProGuest.Groups.Tabs.General.GroupName')}
            </Typography>
            <ValidationRow key={`validation-name`}
                           isSingleItem={true}
                           processingStatus={displayedForm.groupName.processingStatus}>
                <Typography variant="body2">{(displayedForm.groupName as any).name}</Typography>
            </ValidationRow>
        </Grid>
        <Grid item xs={12}>
            <Typography variant="body1" className={classes.sectionTitle}>
                {t('ProGuest.Groups.Tabs.Providers.Title')}
            </Typography>
            <Grid container className={classes.groupDataContainer} spacing={4}>
                {displayedForm.providers.map(provider => providerRow(provider))}
            </Grid>
        </Grid>
        <Grid item xs={12}>
            <Typography variant="body1" className={classes.sectionTitle}>
                {t('ProGuest.Groups.Tabs.Guests.Title')}
            </Typography>
            <Grid container className={classes.groupDataContainer} spacing={4}>
                {displayedForm.guests.map(guest => guestRow(guest))}
            </Grid>
        </Grid>

        <WizardNavigation {...props} onSave={onSave} disabledButtons={getDisabledButtons()}
                          allCompleted={allCompleted}/>

    </Grid>;
};
